import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

const SendGift = ({ selectedUserId, onClose, onGiftSent }) => {
  const [gifts, setGifts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGift, setSelectedGift] = useState(null);
  const [coinBalance, setCoinBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { fetchWithRetry } = useAuth();

  // Lấy số dư coin
  const fetchCoinBalance = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-balance" });
      setCoinBalance(res.data.coinBalance || 0);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể lấy số dư coin!");
    }
  }, [fetchWithRetry]);

  // Lấy danh sách quà tặng
  const fetchGifts = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/gifts" });
      const fetchedGifts = res.data || [];
      // Kiểm tra dữ liệu quà tặng
      const validGifts = fetchedGifts.filter(
        (gift) => gift._id && gift.name && gift.imageUrl && gift.coinPrice
      );
      if (validGifts.length !== fetchedGifts.length) {
        setError("Một số quà tặng không hợp lệ, không thể hiển thị!");
      }
      setGifts(validGifts);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể tải danh sách quà tặng!");
    }
  }, [fetchWithRetry]);

  // Lấy thông tin người nhận
  const fetchUserById = useCallback(async () => {
    if (!selectedUserId || (!selectedUserId.membershipId && !selectedUserId.id)) {
      setError("Không có người nhận được chọn!");
      return;
    }

    try {
      let userData;
      // Thử lấy thông tin người dùng bằng membershipId trước
      if (selectedUserId.membershipId) {
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/users/profile/${selectedUserId.membershipId}`,
        });
        userData = res.data;
      } else {
        // Nếu không có membershipId, lấy bằng _id
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/users/profile-by-id/${selectedUserId.id}`,
        });
        userData = res.data;
      }
      // Kiểm tra dữ liệu người dùng
      if (!userData || !userData._id) {
        throw new Error("Thông tin người nhận không hợp lệ!");
      }
      setSelectedUser(userData);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể tải thông tin người nhận! Người dùng có thể không tồn tại.");
    }
  }, [fetchWithRetry, selectedUserId]);

  useEffect(() => {
    fetchCoinBalance();
    fetchGifts();
    fetchUserById();
  }, [fetchCoinBalance, fetchGifts, fetchUserById]);

  // Kiểm tra dữ liệu hợp lệ
  const isGiftValid = () => {
    return (
      selectedGift &&
      selectedGift._id &&
      selectedGift.name &&
      selectedGift.imageUrl &&
      selectedGift.coinPrice &&
      selectedUser &&
      selectedUser._id &&
      typeof coinBalance === "number" &&
      coinBalance >= selectedGift.coinPrice
    );
  };

  // Gửi quà tặng
  const handleSendGift = async () => {
    // Kiểm tra dữ liệu đầu vào
    if (!selectedUser || !selectedUser._id) {
      setError("Vui lòng chọn người nhận hợp lệ!");
      return;
    }
    if (!selectedGift) {
      setError("Vui lòng chọn một món quà tặng!");
      return;
    }
    if (!selectedGift._id || !selectedGift.name || !selectedGift.imageUrl || !selectedGift.coinPrice) {
      setError("Không thể gửi quà: Thông tin quà tặng không hợp lệ!");
      return;
    }
    if (typeof coinBalance !== "number" || coinBalance < selectedGift.coinPrice) {
      setError(`Không đủ coin để gửi quà tặng này! (Số dư hiện tại: ${coinBalance} coin, Cần: ${selectedGift.coinPrice} coin)`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      // Gọi onGiftSent để gửi quà
      if (onGiftSent) {
        // Truyền dữ liệu gift với thuộc tính _id
        await onGiftSent(
          {
            _id: selectedGift._id,
            name: selectedGift.name,
            imageUrl: selectedGift.imageUrl,
            coinPrice: selectedGift.coinPrice,
          },
          coinBalance
        );
      }

      // Đóng modal nếu không có lỗi
      if (onClose) {
        onClose();
      }
    } catch (error) {
      // Xử lý lỗi từ onGiftSent
      const errorMessage = error.response?.data?.message || "Không thể gửi quà tặng!";
      if (errorMessage.includes("Không đủ coin")) {
        setError(`${errorMessage} (Số dư hiện tại: ${coinBalance} coin, Cần: ${selectedGift.coinPrice} coin)`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}

      <div className="mb-4">
        <p className="text-lg">
          Số dư coin: <span className="font-bold">{coinBalance}</span>
        </p>
      </div>

      {/* Hiển thị thông tin người nhận */}
      {selectedUser && (
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">Người nhận</h3>
          <div className="flex items-center space-x-4">
            <img
              src={
                selectedUser?.primaryPhoto?.photoUrl ||
                selectedUser?.mainImage ||
                selectedUser?.avatar ||
                "https://placehold.co/50"
              }
              alt={selectedUser.name}
              className="w-16 h-16 object-cover rounded-full"
              onError={(e) => (e.target.src = "https://placehold.co/50")}
            />
            <p className="text-sm">{selectedUser.name}</p>
          </div>
        </div>
      )}

      {/* Chọn quà tặng */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">Chọn quà tặng</h3>
        {gifts.length === 0 ? (
          <p className="text-gray-500">Không có quà tặng nào để hiển thị.</p>
        ) : (
          <div className="grid grid-cols-5 gap-4">
            {gifts.map((gift) => (
              <div key={gift._id} className="flex flex-col items-center">
                <input
                  type="radio"
                  name="gift"
                  value={gift._id}
                  checked={selectedGift?._id === gift._id}
                  onChange={() => setSelectedGift(gift)}
                  className="mb-2"
                />
                <img
                  src={gift.imageUrl}
                  alt={gift.name}
                  className="w-16 h-16 object-cover mb-2"
                  onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                />
                <p className="text-sm">{gift.name}</p>
                <p className="text-sm font-bold">{gift.coinPrice} coin</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nút gửi quà tặng */}
      <button
        onClick={handleSendGift}
        className={`bg-blue-500 text-white px-4 py-2 rounded ${
          loading || !isGiftValid() ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"
        }`}
        disabled={loading || !isGiftValid()}
      >
        {loading ? "Đang gửi..." : "Gửi quà tặng"}
      </button>
    </div>
  );
};

export default SendGift;